import { variation } from 'ember-launch-darkly';
import type { ContractFlexDetails } from '@clients/cloud-billing';

//Constants
export const BalanceThresholds = [0.05, 0.25, 0.5];

//Helpers
function roundHundredths(decimal: number) {
  return Math.round(decimal * 100) / 100;
}

export default class FlexBalance {
  // Private fields use number | null for stricter normalization
  #initialAmount?: number | null;
  #estimatedRemainingBalance?: number | null;
  #recognizedConsumedAmount?: number | null;
  #runningTotal?: number | null;

  /**
   * FlexBalance constructor
   * @param flexDetails - Flex contract details (optional)
   * @param estimatedRemainingBalance - Estimated remaining balance (optional)
   */
  constructor(
    flexDetails: ContractFlexDetails | null = {},
    estimatedRemainingBalance?: string | null,
  ) {
    this.#initialAmount = isNaN(parseFloat(flexDetails?.initialAmount ?? ''))
      ? null
      : parseFloat(flexDetails?.initialAmount ?? '');
    this.#recognizedConsumedAmount = isNaN(
      parseFloat(flexDetails?.recognizedConsumedAmount ?? ''),
    )
      ? null
      : parseFloat(flexDetails?.recognizedConsumedAmount ?? '');
    this.#runningTotal = isNaN(parseFloat(flexDetails?.runningTotal ?? ''))
      ? null
      : parseFloat(flexDetails?.runningTotal ?? '');
    this.#estimatedRemainingBalance = isNaN(
      parseFloat(estimatedRemainingBalance ?? ''),
    )
      ? null
      : parseFloat(estimatedRemainingBalance ?? '');
  }

  //Balance Properties
  get estimatedRemainingBalance() {
    return this.#estimatedRemainingBalance ?? null;
  }

  get recognizedConsumedAmount() {
    if (
      typeof this.#recognizedConsumedAmount === 'number' &&
      this.#recognizedConsumedAmount > 0
    ) {
      return this.#recognizedConsumedAmount;
    }
    // If recognizedConsumedAmount doesn't exist or is set to 0 in the Flex consumption pool, default to calculating
    // via the initial amount and estimated remaining balance (which should equal recogized consumed amount).
    if (
      typeof this.#initialAmount === 'number' &&
      typeof this.estimatedRemainingBalance === 'number'
    ) {
      return this.#initialAmount - this.estimatedRemainingBalance;
    }

    // If neither initial amount nor estimated remaining balance is available, return 0
    return 0;
  }

  get isLow() {
    return this.percentageConsumed >= 0.75 && this.percentageConsumed < 1;
  }

  get isZero() {
    return (
      typeof this.estimatedRemainingBalance === 'number' &&
      this.estimatedRemainingBalance <= 0
    );
  }

  get percentageRemaining() {
    const percentage = 1 - this.percentageConsumed;
    return roundHundredths(percentage);
  }

  get percentageConsumed() {
    //TODO: Leaving this temporarily feature-flagged until https://hashicorp.atlassian.net/browse/HCBV2-4333 is complete, after which point all Flex accounts should
    // have recognizedConsumedAmount populated.
    if (variation('hcp-ui-billing-flex-2')) {
      if (
        typeof this.recognizedConsumedAmount === 'number' &&
        typeof this.#initialAmount === 'number' &&
        this.recognizedConsumedAmount <= this.#initialAmount
      ) {
        const percentage = Math.abs(
          (this.recognizedConsumedAmount + (this.#runningTotal ?? 0)) /
            (this.#initialAmount ?? 0),
        );
        return roundHundredths(percentage);
      }
    } else {
      if (
        typeof this.estimatedRemainingBalance === 'number' &&
        typeof this.#initialAmount === 'number' &&
        this.estimatedRemainingBalance <= this.#initialAmount
      ) {
        return Math.abs(
          (this.estimatedRemainingBalance - this.#initialAmount) /
            this.#initialAmount,
        );
      }
    }
    return 0;
  }

  /**
   * Returns the lowest matching balance threshold or -1 if no balance threshold has been matched.
   */
  get balanceThreshold() {
    let matchedThreshold = -1;
    // Since the thresholds are guaranteed to be sorted in ascending order,
    // we can assume that the first matched threshold is the lowest possible match.
    BalanceThresholds.forEach((threshold) => {
      if (
        this.percentageRemaining > 0 &&
        this.percentageRemaining <= threshold &&
        this.percentageRemaining >= threshold - 0.05 // 5% allowance above threshold. (i.e. 50% matches 45% - 50% remaining / 50% - 55% consumed)
      ) {
        matchedThreshold = threshold;
        return matchedThreshold;
      }
    });
    return matchedThreshold;
  }
}
